import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import type { ListKind } from "shared";
import { api } from "../../api";
import { PROGRAM_FIELDS, STATE_FIELDS, snakeToCamel } from "../../admin/fields";
import { RecordForm } from "../../admin/RecordForm";
import { ListEditor } from "../../admin/ListEditor";
import { SourcesPanel } from "../../admin/SourcesPanel";
import { VersionsPanel } from "../../admin/VersionsPanel";
import { STATES } from "../../states";

type Raw = { program: Record<string, unknown> & { id: string; name: string; slug: string };
  lists: Record<ListKind, Record<string, unknown>[]>; states: { id: string; state_code: string; local_name: string | null }[] };
type StateRaw = { variant: Record<string, unknown> & { id: string }; lists: Record<ListKind, Record<string, unknown>[]> };
type Cat = { id: string; name: string; children: { id: string; name: string }[] };
type Dept = { id: string; name: string; acronym: string | null; level: string; state_code: string | null };

const KINDS: { kind: ListKind; title: string }[] = [
  { kind: "contacts", title: "Contacts" }, { kind: "documents", title: "Required documents" }, { kind: "steps", title: "Application steps" },
  { kind: "faqs", title: "FAQs" }, { kind: "denial_reasons", title: "Denial reasons" },
];
const TABS = ["Details", "Lists", "States", "Sources & verification", "History"] as const;

function useOptions() {
  const cats = useQuery({ queryKey: ["categories"], queryFn: () => api<{ categories: Cat[] }>("/api/directory/categories") });
  const depts = useQuery({ queryKey: ["departments"], queryFn: () => api<{ departments: Dept[] }>("/api/directory/departments") });
  const categoryId = (cats.data?.categories ?? []).flatMap((c) => [{ value: c.id, label: c.name }, ...c.children.map((s) => ({ value: s.id, label: `${c.name} › ${s.name}` }))]);
  const all = depts.data?.departments ?? [];
  const label = (d: Dept) => `${d.name}${d.acronym ? ` (${d.acronym})` : ""}${d.state_code ? ` · ${d.state_code}` : ""}`;
  return { categoryId, departments: all.map((d) => ({ value: d.id, label: label(d) })),
    stateDepartments: (code: string) => all.filter((d) => d.state_code === code).map((d) => ({ value: d.id, label: label(d) })) };
}

function StateEditor({ programId, code }: { programId: string; code: string }) {
  const qc = useQueryClient();
  const opts = useOptions();
  const q = useQuery({ queryKey: ["state-raw", programId, code], queryFn: () => api<StateRaw>(`/api/admin/directory/programs/${programId}/states/${code}/raw`) });
  if (!q.data) return <p className="text-sm text-slate-500">Loading…</p>;
  const reload = () => { qc.invalidateQueries({ queryKey: ["state-raw", programId, code] }); qc.invalidateQueries({ queryKey: ["program"] }); };
  return (
    <div className="flex flex-col gap-4">
      <section className="card"><h3 className="mb-3 text-sm font-semibold">{code} details (blank = use the federal value)</h3>
        <RecordForm key={`${programId}-${code}`} fields={STATE_FIELDS} initial={snakeToCamel(q.data.variant)} submitLabel="Save state details"
          selectOptions={{ departmentId: opts.stateDepartments(code) }}
          onSave={async (v) => { await api(`/api/admin/directory/programs/${programId}/states/${code}`, { method: "PUT", body: { ...snakeToCamel(q.data!.variant), ...v } }); reload(); }} />
      </section>
      {KINDS.map(({ kind, title }) => (
        <section key={kind} className="card"><h3 className="mb-3 text-sm font-semibold">{code}: {title}</h3>
          <ListEditor key={`${programId}-${code}-${kind}`} kind={kind} items={q.data!.lists[kind].map(snakeToCamel)}
            onSave={async (items) => { await api(`/api/admin/directory/lists/program/${programId}/${kind}?state=${code}`, { method: "PUT", body: items }); reload(); }} />
        </section>
      ))}
      <section className="card"><SourcesPanel entityType="program_state" entityId={q.data.variant.id} /></section>
      <section className="card"><h3 className="mb-2 text-sm font-semibold">History</h3><VersionsPanel type="program_state" id={q.data.variant.id} /></section>
    </div>
  );
}

export function ProgramEditor() {
  const { id } = useParams({ from: "/app/admin/programs/$id" });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const opts = useOptions();
  const [tab, setTab] = useState<(typeof TABS)[number]>("Details");
  const [stateCode, setStateCode] = useState<string>("");
  const [newState, setNewState] = useState("");
  const [stateErr, setStateErr] = useState("");
  const raw = useQuery({ queryKey: ["program-raw", id], enabled: id !== "new",
    queryFn: () => api<Raw>(`/api/admin/directory/programs/${id}/raw`) });
  const reload = () => { qc.invalidateQueries({ queryKey: ["program-raw", id] }); qc.invalidateQueries({ queryKey: ["programs"] }); qc.invalidateQueries({ queryKey: ["program"] }); };
  const selectOptions = { categoryId: opts.categoryId, departmentId: opts.departments };

  if (id === "new") {
    return (
      <div className="mx-auto max-w-4xl">
        <h1 className="mb-4 text-xl font-semibold">New program</h1>
        <p className="mb-4 text-sm text-slate-500">A new program starts as pending. Add sources and verify it before reps see its details.</p>
        <section className="card">
          <RecordForm fields={PROGRAM_FIELDS} sendAll initial={{ jurisdiction: "federal", status: "pending" }} submitLabel="Create program"
            selectOptions={selectOptions}
            onSave={async (v) => {
              const r = await api<{ id: string }>("/api/admin/directory/programs", { method: "POST", body: v });
              navigate({ to: "/admin/programs/$id", params: { id: r.id } });
            }} />
        </section>
      </div>
    );
  }
  if (raw.isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (raw.error || !raw.data) return <p role="alert" className="text-sm text-red-600">Could not load this program.</p>;
  const d = raw.data;

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-4 flex items-baseline justify-between">
        <h1 className="text-xl font-semibold">Edit: {d.program.name}</h1>
        <Link to="/directory/programs/$slug" params={{ slug: d.program.slug }} search={{}} className="text-sm text-brand-700 hover:underline">View as a rep</Link>
      </div>
      <div role="tablist" className="mb-4 flex gap-1 border-b border-slate-200 dark:border-slate-800">
        {TABS.map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === t ? "border-brand-600 font-semibold text-brand-700" : "border-transparent text-slate-500"}`}>{t}</button>
        ))}
      </div>

      {tab === "Details" && <section className="card">
        <RecordForm key={id} fields={PROGRAM_FIELDS} initial={snakeToCamel(d.program)} submitLabel="Save details" selectOptions={selectOptions}
          onSave={async (v) => { await api(`/api/admin/directory/programs/${id}`, { method: "PATCH", body: v }); reload(); }} />
      </section>}

      {tab === "Lists" && <div className="flex flex-col gap-4">{KINDS.map(({ kind, title }) => (
        <section key={kind} className="card"><h2 className="mb-3 text-sm font-semibold">{title} (all states)</h2>
          <ListEditor key={`${id}--${kind}`} kind={kind} items={d.lists[kind].map(snakeToCamel)}
            onSave={async (items) => { await api(`/api/admin/directory/lists/program/${id}/${kind}`, { method: "PUT", body: items }); reload(); }} />
        </section>
      ))}</div>}

      {tab === "States" && <div className="flex flex-col gap-4">
        <section className="card flex flex-wrap items-end gap-3">
          <div><label className="label" htmlFor="edit-state">State version</label>
            <select id="edit-state" className="input" value={stateCode} onChange={(e) => setStateCode(e.target.value)}>
              <option value="">Choose…</option>{d.states.map((s) => <option key={s.id} value={s.state_code}>{s.state_code}{s.local_name ? ` · ${s.local_name}` : ""}</option>)}
            </select></div>
          <div><label className="label" htmlFor="add-state">Add a state</label>
            <select id="add-state" className="input" value={newState} onChange={(e) => setNewState(e.target.value)}>
              <option value="">Choose…</option>{STATES.filter((s) => !d.states.some((x) => x.state_code === s.code)).map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select></div>
          <button className="btn-primary" disabled={!newState} onClick={async () => {
            try {
              await api(`/api/admin/directory/programs/${id}/states/${newState}`, { method: "PUT", body: {} });
              setStateErr(""); setStateCode(newState); setNewState(""); reload();
            } catch (e) { setStateErr((e as Error).message); }
          }}>Add</button>
          {stateCode && <button className="btn-ghost text-red-600" onClick={async () => {
            if (!window.confirm(`Delete the ${stateCode} version and its lists?`)) return;
            try {
              await api(`/api/admin/directory/programs/${id}/states/${stateCode}`, { method: "DELETE" });
              setStateErr(""); setStateCode(""); reload();
            } catch (e) { setStateErr((e as Error).message); }
          }}>Delete {stateCode} version</button>}
          {stateErr && <span role="alert" className="text-sm text-red-600">{stateErr}</span>}
        </section>
        {stateCode && <StateEditor key={stateCode} programId={id} code={stateCode} />}
      </div>}

      {tab === "Sources & verification" && <section className="card"><SourcesPanel entityType="program" entityId={id} /></section>}
      {tab === "History" && <section className="card"><VersionsPanel type="program" id={id} /></section>}
    </div>
  );
}
